import { Btn, Bx, IconBtn, Typo } from '@curry-group/mui-curcuma';
import { faDownload, faPoll, faRedo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../data/api';
import { fetchSurveys, ISurveyAdminItem } from '../../data/reducer/userprofile';
import { IListItem } from '../../model/list/ListItem';
import { ThingTypes } from '../../model/ryve/Thing';
import { NotFoundComponent } from '../detail/not-found';
import { ListWidget } from '../widget/list';

const resultCaption = (s: ISurveyAdminItem) => {
  const answers = `${s.answerCount} Antwort${s.answerCount === 1 ? '' : 'en'}`;
  return s.declinedCount > 0 ? `${answers}, ${s.declinedCount} x abgelehnt` : answers;
};

const toListItem = (s: ISurveyAdminItem): IListItem => ({
  _id: s.id,
  __original: s,
  typeId: ThingTypes.Survey,
  // The survey has no detail page in the frontend — it is authored in the backoffice.
  to: false,
  title: s.title || s.question || '(ohne Titel)',
  type: s.active
    ? 'Aktive Umfrage'
    : s.closed
    ? 'Geschlossen'
    : s.published
    ? 'Veröffentlicht'
    : 'Unveröffentlicht',
  color: s.active ? 'success' : 'primary',
  highlight: s.active,
  avatar: { type: 'icon', icon: faPoll },
  subtitle: resultCaption(s),
  subtitle_2: s.question,
  timestamp: s.createdAt,
  // Dims surveys that are not collecting answers, the same way deactivated
  // communities are shown.
  isDeactivated: !s.published || s.closed,
});

/**
 * Survey overview with a per-survey CSV export of the results, so results do not
 * have to be filtered and exported in the backoffice.
 */
export const SurveyExportAdminComponent: React.FC = () => {
  const dispatch = useDispatch();
  const isAdmin = useSelector(state => state.foundation?.profile?.isAdmin);
  const surveys = useSelector(state => state.userProfile?.surveys) || [];
  const working = useSelector(state => state.userProfile?.surveysWorking);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    dispatch(fetchSurveys({}));
  }, [dispatch, isAdmin]);

  if (!isAdmin) {
    return <NotFoundComponent />;
  }

  const downloadResults = async (survey: ISurveyAdminItem) => {
    setDownloadingId(survey.id);
    setDownloadError(null);
    try {
      const response = await fetch(api.params(api.v2.admin.surveyExport, { id: survey.id }));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      // The file name is set by the backend via Content-Disposition — mirror it here so
      // the download keeps the survey name.
      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const fileName = match?.[1] || 'Umfrage.csv';
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      document.body.appendChild(link);
      link.download = fileName;
      link.href = url;
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError('Export fehlgeschlagen. Bitte erneut versuchen.');
    }
    setDownloadingId(null);
  };

  const listItems: IListItem[] = surveys.map(toListItem);

  return (
    <Bx pb={4}>
      <Bx pb={2} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={8}>
        <Typo variant={'h3'} component={'h3'}>
          Umfragen
        </Typo>
        <Btn
          size="medium"
          variant="outlined"
          color="primary"
          disabled={!!working}
          startIcon={<FontAwesomeIcon icon={faRedo} />}
          onClick={() => dispatch(fetchSurveys({}))}
          title="Liste neu laden"
        >
          {working ? 'Lade…' : 'Aktualisieren'}
        </Btn>
      </Bx>
      <Bx pb={2}>
        <Typo variant="body2" color="textSecondary">
          Umfragen werden im Backoffice angelegt und veröffentlicht. Angezeigt wird immer die zuletzt angelegte
          veröffentlichte Umfrage, die nicht geschlossen ist. Wird sie im Backoffice als „geschlossen“ markiert, rückt
          die nächste veröffentlichte Umfrage nach; die Ergebnisse bleiben erhalten. Der Export enthält pro Nutzer eine
          Zeile und pro Antwortmöglichkeit eine Spalte; die Nutzer sind pseudonymisiert (USER_1, USER_2, …).
        </Typo>
      </Bx>
      {!!downloadError && (
        <Bx pb={2}>
          <Typo variant="body2" color="error">
            {downloadError}
          </Typo>
        </Bx>
      )}
      <Bx mt={2} maxHeight={480} style={{ overflowY: 'auto' }}>
        {listItems.length > 0 ? (
          <ListWidget
            working={working}
            items={listItems}
            headline={`${listItems.length} Umfrage${listItems.length === 1 ? '' : 'n'}`}
            itemActions={item => {
              const survey = item.__original as ISurveyAdminItem;
              const hasResults = survey.answerCount + survey.declinedCount > 0;
              const isDownloading = downloadingId === survey.id;
              const tooltip = !hasResults
                ? 'Keine Ergebnisse vorhanden'
                : isDownloading
                ? 'Export läuft…'
                : 'Ergebnisse als CSV exportieren';
              return [
                <Bx key={'btnActions'} display="flex" flexGrow={1} justifyContent="flex-end" alignItems="center" gridGap={8}>
                  {/* The title sits on the wrapper: a disabled button receives no hover
                      events, so the tooltip would not show on the button itself. */}
                  <span title={tooltip} style={{ display: 'inline-flex' }}>
                    <IconBtn
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        downloadResults(survey);
                      }}
                      disabled={isDownloading || !hasResults}
                      color="primary"
                      size="medium"
                      variant="outlined"
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </IconBtn>
                  </span>
                </Bx>,
              ];
            }}
          />
        ) : (
          <ListWidget ignoreWorking={!working} items={undefined} headline={working ? 'Lade…' : 'Keine Umfragen vorhanden'} />
        )}
      </Bx>
    </Bx>
  );
};
